import { FC, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import cn from 'classnames'
import accounting from 'accounting'
import groupBy from 'lodash/groupBy'

import { useCheckMobile } from '@/hooks/useCheckMobile'

import Loading from '@/components/Loading'

import { getWorkOrders } from '@/common/graphql/getWorkOrders'

import {
  STATUS,
  WORK_ORDER_STATUS,
  WORK_ORDER_PRIORITY_STYLE,
  WORK_ORDER_STATUS_STYLE,
  WORK_ORDER_STATUS_TABS,
  CARD_SELECT_TYPE,
} from '../../constants'

import { WorkOrder, WorkOrderData } from '@/common/types'

export interface WorkOrderModalContentProps {
  closeModal: Function
  handleOnClick: Function
}

const WorkOrderModalContent: FC<WorkOrderModalContentProps> = ({
  closeModal,
  handleOnClick,
}) => {
  const firstTab = Object.values(WORK_ORDER_STATUS_TABS)[0]
  const [activeBtnFilter, setActiveBtnFilter] = useState(firstTab)

  const [workOrderData, setWorkOrderData] = useState<WorkOrderData | null>(null)
  const isMobile = useCheckMobile()

  const { loading, data } = useQuery(getWorkOrders, {
    fetchPolicy: 'cache-and-network',
    variables: {
      input: {
        statusFilter: 'ALL',
      },
    },
  })

  useEffect(() => {
    if (data) {
      const workStatusGroup: { [key: string]: WorkOrder[] } = {
        [WORK_ORDER_STATUS_TABS[STATUS.PENDING_ESTIMATE]]: [],
        [WORK_ORDER_STATUS_TABS[STATUS.FOR_OWNER_APPROVAL]]: [],
        [WORK_ORDER_STATUS_TABS[STATUS.UPCOMING_VISIT]]: [],
        [WORK_ORDER_STATUS_TABS.Closed]: [],
        [WORK_ORDER_STATUS_TABS[STATUS.DEFERRED]]: [],
      }

      groupByWorkStatus(workStatusGroup)

      const groupedByStatusAndProp: WorkOrderData = {}
      Object.entries(workStatusGroup).forEach(([groupName, workOrders]) => {
        groupedByStatusAndProp[groupName] = groupBy(workOrders, 'propertyID')
      })

      setWorkOrderData(groupedByStatusAndProp)
    }
  }, [data])

  const groupByWorkStatus = (workStatusGroup: {
    [key: string]: WorkOrder[]
  }) => {
    const { workOrders } = data || { workOrders: [] }

    workOrders.forEach((item: WorkOrder) => {
      let groupKeyName = null

      switch (item.workStatus) {
        case STATUS.PENDING_ESTIMATE:
          groupKeyName = WORK_ORDER_STATUS_TABS[STATUS.PENDING_ESTIMATE]
          break
        case STATUS.FOR_OWNER_APPROVAL:
          groupKeyName = WORK_ORDER_STATUS_TABS[STATUS.FOR_OWNER_APPROVAL]
          break
        case STATUS.IN_PROGRESS:
        case STATUS.UPCOMING_VISIT:
          groupKeyName = WORK_ORDER_STATUS_TABS[STATUS.UPCOMING_VISIT]
          break
        case STATUS.COMPLETE:
        case STATUS.COMPLETE_BILLED:
        case STATUS.COMPLETE_NOT_BILLED:
          groupKeyName = WORK_ORDER_STATUS_TABS.Closed
          break
        case STATUS.DEFERRED:
          groupKeyName = WORK_ORDER_STATUS_TABS[STATUS.DEFERRED]
          break
        case STATUS.CANCELLED:
          groupKeyName = WORK_ORDER_STATUS_TABS.Closed
          break
        default:
          break
      }

      if (groupKeyName) {
        workStatusGroup[groupKeyName].push(item)
      }
    })
  }

  const EmptyState = () => {
    return (
      <div className="pmui-h-full pmui-w-full pmui-flex pmui-flex-col pmui-items-center pmui-justify-center ">
        <span className="icon icon-ic-maintenance-empty pmui-text-4xl"></span>
        <span className="pmui-text-lg pmui-font-semibold">{`No work orders yet`}</span>
      </div>
    )
  }

  return (
    <>
      <div className="pmui-flex pmui-justify-between pmui-items-center pmui-font-semibold pmui-px-4 pmui-pt-4">
        <h1>Work Orders</h1>
        <button
          className={cn(
            'pmui-flex pmui-justify-center pmui-items-center pmui-cursor-pointer pmui-w-10 pmui-h-10',
            'pmui-text-xl pmui-text-pop-gray-500 icon icon-close-round',
          )}
          onClick={e => {
            e.preventDefault()
            closeModal()
          }}
        />
      </div>

      <div className="pmui-mb-6 pmui-h-full">
        {loading || !workOrderData ? (
          <div className="pmui-w-full pmui-h-full">
            <Loading noMessage />
          </div>
        ) : (
          <div className="pmui-mt-4 pmui-py-2 pmui-h-full">
            <div className="pmui-flex pmui-w-full pmui-whitespace-nowrap pmui-overflow-x-auto pmui-px-4">
              {Object.entries(WORK_ORDER_STATUS_TABS).map(([name, value]) => {
                return (
                  <button
                    key={`tab-${name}`}
                    className={cn(
                      'pmui-rounded-full pmui-py-2 pmui-px-3 focus:pmui-outline-none pmui-text-sm pmui-font-semibold pmui-mr-4',
                      activeBtnFilter === value
                        ? 'pmui-text-white pmui-bg-pop-orange'
                        : 'pmui-text-pop-gray-300 pmui-bg-gray-100',
                    )}
                    onClick={() => {
                      setActiveBtnFilter(value)
                    }}
                  >
                    {name}
                  </button>
                )
              })}
            </div>
            <div className="pmui-modal-scroll-container pmui-mt-4 pmui-pb-28 pmui-px-4">
              {Object.entries(workOrderData[activeBtnFilter]).length === 0 ? (
                <EmptyState />
              ) : (
                <>
                  {Object.entries(workOrderData[activeBtnFilter]).map(
                    ([propertyID, workOrders], index) => {
                      return (
                        <div key={`${propertyID}-${index}`}>
                          <div className="pmui-font-rubik pmui-font-bold pmui-mb-4 pmui-mt-8">
                            {`${workOrders[0]?.street}${
                              workOrders[0]?.unitNumber
                                ? `, Unit ${workOrders[0]?.unitNumber}`
                                : ''
                            }`}
                          </div>
                          {workOrders.map(workOrder => {
                            return (
                              <button
                                key={workOrder.workOrderNumber}
                                className={cn(
                                  'pmui-my-4 pmui-h-40 pmui-flex pmui-flex-col pmui-flex-shrink-0 hover:pmui-bg-gray-100  pmui-cursor-pointer pmui-p-3 \
                               pmui-text-sm pmui-text-left pmui-rounded-lg carousel-box pmui-w-full',
                                )}
                                onClick={() => {
                                  const actions = [
                                    {
                                      type: 'postback',
                                      text: workOrder.workOrderNumber,
                                      payload: '',
                                      uri: '',
                                    },
                                  ]
                                  handleOnClick(actions, {
                                    ...workOrder,
                                    title: `${workOrder.caseType} - ${workOrder.location}`,
                                    type: CARD_SELECT_TYPE.WORK_ORDER,
                                  })
                                  closeModal()
                                }}
                              >
                                <div className="pmui-flex pmui-justify-between pmui-items-center pmui-w-full">
                                  <div>{workOrder.workOrderNumber}</div>
                                  {workOrder.workStatus && (
                                    <div
                                      className={cn(
                                        `pmui-px-2 pmui-py-2 pmui-rounded pmui-text-xs`,
                                        WORK_ORDER_STATUS_STYLE[
                                          workOrder.workStatus
                                        ],
                                      )}
                                    >
                                      {WORK_ORDER_STATUS[workOrder.workStatus]}
                                    </div>
                                  )}
                                </div>
                                <div className="pmui-w-full pmui-text-base pmui-font-semibold pmui-mt-1 pmui-flex-1">
                                  {`${workOrder.caseType} - ${workOrder.location}`}
                                </div>
                                <div className="pmui-flex pmui-justify-between pmui-items-center pmui-w-full">
                                  <div className="pmui-flex pmui-items-center">
                                    <div
                                      className={cn(
                                        'dot pmui-mr-2',
                                        WORK_ORDER_PRIORITY_STYLE[
                                          workOrder.priority || 'Common'
                                        ],
                                      )}
                                    />
                                    <div
                                      className={
                                        'pmui-text-sm pmui-text-pop-gray-300'
                                      }
                                    >
                                      {workOrder.priority || 'Common'}
                                    </div>
                                  </div>
                                  <div className={'pmui-text-sm'}>
                                    Total Est:{' '}
                                    {accounting.formatMoney(
                                      workOrder.grandTotal || 0,
                                      '$',
                                    )}
                                  </div>
                                </div>
                                <div className="horizontal-divider pmui-my-2 pmui-w-full" />
                                <div className="pmui-text-center pmui-w-full">
                                  <Link
                                    to={{
                                      pathname: `/owner/maintenance/${workOrder.propertyID}/wo/${workOrder.workOrderID}`,
                                      state: {
                                        chatWidgetOpen: isMobile ? false : true,
                                      },
                                    }}
                                    onClick={e => {
                                      e.stopPropagation()
                                      closeModal()
                                    }}
                                  >
                                    <div
                                      className="sm:pmui-text-base xs:pmui-text-sm pmui-font-eina03 pmui-font-semibold \
                              pmui-text-pop-orange pmui-whitespace-pre-wrap hover:pmui-cursor-pointer hover:pmui-underline"
                                    >
                                      VIEW
                                    </div>
                                  </Link>
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      )
                    },
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </>
  )
}

export default WorkOrderModalContent
